import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Heart, MessageCircle, Share2, Play, Pause, Volume2, VolumeX,
  Radio, Calendar, Sparkles, ArrowLeft, TrendingUp
} from "lucide-react";
import { useVibeFeed, VibeFeedItem } from "@/hooks/useVibeFeed";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import BottomNav from "@/components/BottomNav";

const VibeFeed = () => {
  const navigate = useNavigate();
  const { feedItems, loading, likeItem } = useVibeFeed();
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle scroll to detect current item
  const handleScroll = () => {
    if (!containerRef.current) return;
    const scrollTop = containerRef.current.scrollTop;
    const itemHeight = window.innerHeight;
    const newIndex = Math.round(scrollTop / itemHeight);
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < feedItems.length) {
      setCurrentIndex(newIndex);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [currentIndex, feedItems.length]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="w-10 h-10 rounded-full border-4 border-pop border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-black relative">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 p-4 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <h1 className="text-xl font-bold text-white">Vibe Feed</h1>
          <div className="w-10" />
        </div>
        
        {/* Tab filters */}
        <div className="flex gap-4 mt-4 justify-center">
          <button className="text-white font-semibold border-b-2 border-white pb-1">
            For You
          </button>
          <button className="text-white/60 font-medium pb-1">
            Following
          </button>
          <button className="text-white/60 font-medium pb-1">
            Live Now
          </button>
        </div>
      </div>

      {/* Feed */}
      <div 
        ref={containerRef}
        className="h-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
      >
        {feedItems.map((item, index) => (
          <FeedItemCard 
            key={item.id} 
            item={item} 
            isActive={index === currentIndex}
            onLike={() => likeItem(item.id)}
          />
        ))}
      </div>

      <BottomNav />
    </div>
  );
};

interface FeedItemCardProps {
  item: VibeFeedItem;
  isActive: boolean;
  onLike: () => void;
}

const FeedItemCard: React.FC<FeedItemCardProps> = ({ item, isActive, onLike }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);

  const handleLike = () => {
    if (!isLiked) {
      setIsLiked(true);
      onLike();
    }
  };

  const getTypeIcon = () => {
    switch (item.item_type) {
      case "highlight":
        return <Sparkles className="w-4 h-4" />;
      case "match_story":
        return <Heart className="w-4 h-4" />;
      case "trending_arena":
        return <TrendingUp className="w-4 h-4" />;
      case "scheduled_arena":
        return <Calendar className="w-4 h-4" />;
      default:
        return <Radio className="w-4 h-4" />;
    }
  };

  const getTypeLabel = () => {
    switch (item.item_type) {
      case "highlight":
        return "Highlight";
      case "match_story":
        return "Match Story";
      case "trending_arena":
        return "Trending";
      case "scheduled_arena":
        return "Upcoming";
      default:
        return "Moment";
    }
  };

  return (
    <div className="h-screen w-full snap-start relative flex items-center justify-center">
      {/* Background image/video */}
      <div className="absolute inset-0">
        <img
          src={item.thumbnail_url || "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&h=700&fit=crop"}
          alt=""
          className="w-full h-full object-cover"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/80" />
      </div>

      {/* Type badge */}
      <div className="absolute top-24 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium">
        {getTypeIcon()}
        {getTypeLabel()}
      </div>

      {/* Play/Pause indicator */}
      {item.video_url && (
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="absolute inset-0 flex items-center justify-center z-10"
        >
          {!isPlaying && (
            <div className="w-20 h-20 rounded-full bg-black/50 flex items-center justify-center">
              <Play className="w-10 h-10 text-white fill-white ml-1" />
            </div>
          )}
        </button>
      )}

      {/* Right side actions */}
      <div className="absolute right-4 bottom-32 flex flex-col items-center gap-6">
        {/* Like */}
        <button 
          onClick={handleLike}
          className="flex flex-col items-center gap-1"
        >
          <div className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-all",
            isLiked ? "bg-pink-500 scale-110" : "bg-white/20 backdrop-blur-sm"
          )}>
            <Heart className={cn(
              "w-6 h-6 transition-all",
              isLiked ? "text-white fill-white" : "text-white"
            )} />
          </div>
          <span className="text-white text-xs font-medium">
            {item.engagement_count + (isLiked ? 1 : 0)}
          </span>
        </button>

        {/* Comment */}
        <button className="flex flex-col items-center gap-1">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <span className="text-white text-xs font-medium">Reply</span>
        </button>

        {/* Share */}
        <button className="flex flex-col items-center gap-1">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Share2 className="w-6 h-6 text-white" />
          </div>
          <span className="text-white text-xs font-medium">Share</span>
        </button>

        {/* Mute/Unmute */}
        <button 
          onClick={() => setIsMuted(!isMuted)}
          className="flex flex-col items-center gap-1"
        >
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            {isMuted ? (
              <VolumeX className="w-6 h-6 text-white" />
            ) : (
              <Volume2 className="w-6 h-6 text-white" />
            )}
          </div>
        </button>
      </div>

      {/* Bottom content */}
      <div className="absolute bottom-24 left-4 right-20">
        <h2 className="text-white text-xl font-bold mb-2 drop-shadow-lg">
          {item.title}
        </h2>
        {item.description && (
          <p className="text-white/90 text-sm mb-3 line-clamp-2 drop-shadow">
            {item.description}
          </p>
        )}
        <p className="text-white/60 text-xs">
          {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
        </p>

        {/* CTA for different types */}
        {item.item_type === "trending_arena" && (
          <button className="mt-3 px-4 py-2 bg-gradient-to-r from-pop to-primary rounded-full text-white text-sm font-semibold">
            Join Now
          </button>
        )}
        {item.item_type === "scheduled_arena" && (
          <button className="mt-3 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-semibold">
            RSVP
          </button>
        )}
      </div>
    </div>
  );
};

export default VibeFeed;
